import React, { useState, useEffect, useCallback } from 'react';
import { conferencesAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, X, Save, Globe, MapPin } from 'lucide-react';

const AdminConferences = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingConf, setEditingConf] = useState(null);
  const [activeTab, setActiveTab] = useState('international');
  const [form, setForm] = useState({
    name: '', type: 'international', country: '', location: '', date: '', role: '', description: '', blog_slug: ''
  });
  const [error, setError] = useState(null);

  const fetchConferences = useCallback(async () => {
    try {
      setLoading(true);
      const data = await conferencesAPI.getAll();
      setConferences(data || []);
    } catch { setError('Failed to load conferences'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConferences(); }, [fetchConferences]);

  const resetForm = () => {
    setForm({ name: '', type: activeTab, country: '', location: '', date: '', role: '', description: '', blog_slug: '' });
    setEditingConf(null);
    setShowForm(false);
  };

  const handleEdit = (conf) => {
    setForm({
      name: conf.name, type: conf.type || 'international', country: conf.country || '',
      location: conf.location, date: conf.date, role: conf.role || '',
      description: conf.description, blog_slug: conf.blog_slug || '',
    });
    setEditingConf(conf);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, images: [] };
    try {
      if (editingConf) {
        await conferencesAPI.update(editingConf.id, payload);
      } else {
        await conferencesAPI.create(payload);
      }
      resetForm();
      fetchConferences();
    } catch (err) { alert('Error saving conference'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this conference?')) return;
    try {
      await conferencesAPI.delete(id);
      fetchConferences();
    } catch (err) { alert('Error deleting conference'); }
  };

  const filtered = conferences.filter(c => c.type === activeTab);

  return (
    <div data-testid="admin-conferences">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Conferences</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-teal-700 hover:bg-teal-600" data-testid="add-conference-button">
          <Plus className="w-4 h-4 mr-2" /> Add Conference
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{editingConf ? 'Edit' : 'New'} Conference</h2>
              <button onClick={resetForm} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Conference Name *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-800 border-slate-700 text-white" required data-testid="conf-name-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Type</Label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full h-10 rounded-md border bg-slate-800 border-slate-700 text-white px-3 text-sm" data-testid="conf-type-select">
                    <option value="international">International</option>
                    <option value="national">National</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {form.type === 'international' && (
                  <div>
                    <Label className="text-slate-300">Country</Label>
                    <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="conf-country-input" />
                  </div>
                )}
                <div>
                  <Label className="text-slate-300">Location *</Label>
                  <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-slate-800 border-slate-700 text-white" required data-testid="conf-location-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Date *</Label>
                  <Input value={form.date} onChange={e => setForm({...form, date: e.target.value})} placeholder="e.g. 2024 or March 2024" className="bg-slate-800 border-slate-700 text-white" required data-testid="conf-date-input" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Your Role</Label>
                <Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} placeholder="e.g. Keynote Speaker, Guest Faculty" className="bg-slate-800 border-slate-700 text-white" data-testid="conf-role-input" />
              </div>
              <div>
                <Label className="text-slate-300">Description *</Label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full min-h-[100px] rounded-md border bg-slate-800 border-slate-700 text-white px-3 py-2 text-sm" required data-testid="conf-description-input" />
              </div>
              <div>
                <Label className="text-slate-300">Related Blog Post Slug (optional - link to a blog post with photos)</Label>
                <Input value={form.blog_slug} onChange={e => setForm({...form, blog_slug: e.target.value})} placeholder="e.g. dhaka-cancer-summit-2024" className="bg-slate-800 border-slate-700 text-white" data-testid="conf-blog-slug-input" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-teal-700 hover:bg-teal-600" data-testid="conf-save-button">
                  <Save className="w-4 h-4 mr-2" /> {editingConf ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm} className="text-slate-400">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800 mb-4">
          <TabsTrigger value="international" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white text-slate-400">International</TabsTrigger>
          <TabsTrigger value="national" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white text-slate-400">National</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-400 py-12">No {activeTab} conferences yet.</div>
          ) : (
            <div className="space-y-3">
              {filtered.map(conf => (
                <Card key={conf.id} className="bg-slate-900 border-slate-800" data-testid={`conf-item-${conf.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium truncate">{conf.name}</h3>
                        {conf.country && <Badge variant="outline" className="text-xs border-slate-700 text-teal-400"><Globe className="w-3 h-3 mr-1" />{conf.country}</Badge>}
                        {conf.role && <Badge className="bg-slate-800 text-slate-300 text-xs">{conf.role}</Badge>}
                      </div>
                      <div className="flex items-center gap-3 text-slate-500 text-sm">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{conf.location}</span>
                        <span>{conf.date}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(conf)} className="text-slate-400 hover:text-white">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(conf.id)} className="text-slate-400 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminConferences;
